import { AppBar, Toolbar, IconButton, Typography, Box } from '@mui/material';
import { ArrowBack, Favorite, FavoriteBorder, Share } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import offerDetailService from '../services/offerDetailService';

interface OfferDetailHeaderProps {
  offerId: string;
  isFavorite: boolean;
  onFavoriteToggle: () => void;
  onShare?: () => void;
  shareDisabled?: boolean;
  onBack?: () => void;
  isOwner?: boolean;
}

export default function OfferDetailHeader({
  offerId,
  isFavorite,
  onFavoriteToggle,
  onShare,
  shareDisabled,
  onBack,
  isOwner,
}: Readonly<OfferDetailHeaderProps>) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const [isUpdating, setIsUpdating] = useState(false);

  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/');
  };

  const handleFavoriteClick = async () => {
    try {
      setIsUpdating(true);
      await offerDetailService.toggleFavorite(offerId, isFavorite);
      onFavoriteToggle();
      showSnackbar(
        isFavorite ? t('offerDetail.removeFromFavorites') : t('offerDetail.addToFavorites'),
        { variant: 'success' }
      );
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number } };
      if (apiError.response?.status === 401) {
        showSnackbar('Please log in to save favorites', { variant: 'warning' });
        // Optionally redirect to login
      } else {
        showSnackbar('Failed to update favorites', { variant: 'error' });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: 'primary.main',
        borderRadius: '0 0 16px 16px',
        zIndex: theme => theme.zIndex.appBar,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleBack}
          aria-label={t('offerDetail.back')}
        >
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h6"
          component="div"
          sx={{ color: 'white', fontWeight: 500, flex: 1, textAlign: 'center' }}
        >
          {isOwner ? t('offerDetail.titleOwner') : t('offerDetail.title')}
        </Typography>

        <Box sx={{ width: 96, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <IconButton
            color="inherit"
            onClick={onShare}
            disabled={shareDisabled}
            aria-label={t('offerDetail.shareAria')}
            data-testid="offer-detail-header-share-button"
          >
            <Share />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleFavoriteClick}
            disabled={isUpdating}
            aria-label={
              isFavorite ? t('offerDetail.removeFromFavorites') : t('offerDetail.addToFavorites')
            }
            data-testid="offer-detail-header-favorite-button"
          >
            {isFavorite ? <Favorite sx={{ color: 'secondary.main' }} /> : <FavoriteBorder />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
